import React from 'react';
import { useIntl } from 'react-intl';

import Review, { IProps as IReviewProps } from '@/components/route/listing/OwnerReviews/Review';
import Rating from '@/components/route/listing/Rating/Rating';
import Divider, { DividerTone } from '@/components/switchback/Divider/Divider';
import Heading from '@/components/switchback/Heading/Heading';
import { IAverageRatings, IAverageReviews } from '@/services/types/search/rentals/id';

import CategoryReviewsRating from '../Reviews/CategoryReviewsRating';

export interface IReview extends Omit<IReviewProps, 'imagesToShow' | 'onLightboxChange'> {
  id: number;
  exclude_from_rating?: boolean;
}

interface IProps {
  totalReviews: number;
  averageRating: number;
  reviews: IReview[];
  categoryReviewsRatings?: IAverageReviews;
  ownerAverageRating?: IAverageRatings;
  isStay?: boolean;
  isCampground?: boolean;
  onLightboxChange: (id: number, imageIndex: number | null) => void;
}

const AllReviews: React.FC<IProps> = ({
  totalReviews,
  averageRating,
  reviews,
  categoryReviewsRatings,
  ownerAverageRating,
  isStay,
  isCampground,
  onLightboxChange,
}) => {
  const intl = useIntl();

  return (
    <>
      <div className="flex items-baseline mb-8 col-start-content col-end-content">
        <Heading level={2} className="text-3xl highlight">
          {intl.formatMessage(
            { defaultMessage: '{reviewsAmount} reviews', id: 'yKF5Lm' },
            { reviewsAmount: totalReviews },
          )}
        </Heading>
        <span className="flex ml-2 font-bold text-gray-800 text-300 md:text-800 md:highlight md:font-normal">
          {'('}
          <Rating iconClassName="h-3 w-3 md:h-6 md:w-6" number={averageRating} />
          {')'}
        </span>
      </div>
      {!!categoryReviewsRatings &&
        (categoryReviewsRatings.owner?.length || categoryReviewsRatings?.rental?.length) && (
          <div className="col-start-content col-span-6 md:col-start-content md:col-span-10 lg:col-start-content lg:col-span-10">
            <div className="mb-8 md:col-span-10 lg:col-span-5">
              <CategoryReviewsRating
                averageRatings={categoryReviewsRatings}
                ownerAverageRating={ownerAverageRating}
                isStay={isStay}
                isCampground={isCampground}
                reviews={reviews}
              />
            </div>
          </div>
        )}
      {reviews.map((review, index) => {
        const isLastItem = index + 1 === reviews.length;
        return (
          <div
            key={review.id}
            className="col-start-content col-span-6 md:col-start-content md:col-span-10 lg:col-start-content lg:col-span-10">
            <Review
              name={review.exclude_from_rating ? 'Outdoorsy' : review.name}
              date={review.date}
              photoUrl={review.exclude_from_rating ? '' : review.photoUrl}
              ratingNumber={review.ratingNumber}
              review={review.review}
              images={review.images}
              tags={review.tags}
              ownerAnswer={review.ownerAnswer}
              openedImage={review.openedImage}
              onLightboxChange={imageIndex => onLightboxChange(review.id, imageIndex)}
              shouldRenderOutdoorsyLogo={review.exclude_from_rating}
            />
            {!isLastItem && <Divider tone={DividerTone.dark} className="my-8 md:my-11" />}
          </div>
        );
      })}
    </>
  );
};

export default AllReviews;
